import { useRef, useState, useMemo, useCallback, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Tooltip } from 'primereact/tooltip';
import * as XLSX from 'xlsx';

// ---------- Types ----------

type RowKind = 'new-group' | 'changed' | 'unchanged';

interface ParsedRow {
  payer: string;
  group: string;
  rowKind: RowKind;
}

interface ParseWarning {
  row: number;
  message: string;
}

interface ParseResult {
  /** Rows that matched an existing payer and will be applied */
  rows: ParsedRow[];
  warnings: ParseWarning[];
  newGroups: string[];
  /** Count of uploaded payers that were skipped (not in existing data) */
  skippedCount: number;
}

export interface UploadFooterActions {
  onDiscard: () => void;
  onApply: () => void;
  canApply: boolean;
  onUploadDifferent: () => void;
}

export interface PayerGroupingUploadProps {
  existingPayers: { insurancePayerPrimary: string; assignedMasterPayorGroup: string }[];
  existingGroups: string[];
  onApply: (
    rows: { insurancePayerPrimary: string; assignedMasterPayorGroup: string }[],
    groups: string[],
  ) => void;
  onUploadStateChange?: (state: UploadFooterActions | null) => void;
}

// ---------- Column-detection helpers ----------

const PAYER_ALIASES = ['payer', 'insurance payer', 'insurancepayerprimary', 'payer name', 'insurance_payer_primary'];
const GROUP_ALIASES = ['master payer', 'master payer group', 'masterpayergroup', 'group', 'master_payer_group', 'payer group'];

const normalize = (s: string) => s.trim().toLowerCase().replace(/[_\-]/g, ' ');

const detectColumns = (
  headers: string[],
): { payerIdx: number; groupIdx: number } | null => {
  let payerIdx = -1;
  let groupIdx = -1;

  for (let i = 0; i < headers.length; i++) {
    const h = normalize(headers[i]);
    if (payerIdx === -1 && PAYER_ALIASES.includes(h)) payerIdx = i;
    if (groupIdx === -1 && GROUP_ALIASES.includes(h)) groupIdx = i;
  }

  // Fallback: first two columns
  if (payerIdx === -1 && groupIdx === -1 && headers.length >= 2) {
    return { payerIdx: 0, groupIdx: 1 };
  }

  if (payerIdx === -1 || groupIdx === -1) return null;
  return { payerIdx, groupIdx };
};

// ---------- Parse logic ----------

const MAX_GROUP_LEN = 100;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const parseWorkbook = (
  wb: XLSX.WorkBook,
  existingGroups: string[],
  existingPayerNames: Set<string>,
): ParseResult => {
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const raw: string[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });

  if (raw.length === 0) {
    return { rows: [], warnings: [{ row: 0, message: 'File contains no data.' }], newGroups: [], skippedCount: 0 };
  }

  // Detect header row
  const headerRow = raw[0].map(String);
  const cols = detectColumns(headerRow);
  if (!cols) {
    return {
      rows: [],
      warnings: [{ row: 1, message: 'Could not detect "Payer" and "Group" columns. Expected headers like "Insurance Payer" and "Master Payer Group".' }],
      newGroups: [],
      skippedCount: 0,
    };
  }

  const warnings: ParseWarning[] = [];
  const seenPayers = new Map<string, number>(); // lowercase payer → index in rows
  const rows: ParsedRow[] = [];
  const groupSet = new Set(existingGroups.map((g) => g.toLowerCase()));
  const newGroupSet = new Set<string>();
  let skippedCount = 0;

  for (let i = 1; i < raw.length; i++) {
    const payerRaw = String(raw[i][cols.payerIdx] ?? '').trim();
    let groupRaw = String(raw[i][cols.groupIdx] ?? '').trim();

    // Skip fully blank rows
    if (!payerRaw && !groupRaw) continue;

    // Blank payer → skip with warning
    if (!payerRaw) {
      warnings.push({ row: i + 1, message: 'Blank payer name — row skipped.' });
      continue;
    }

    // Skip payers that don't exist in the practice data
    if (!existingPayerNames.has(payerRaw.toLowerCase())) {
      warnings.push({ row: i + 1, message: `"${payerRaw}" is not in your payer data — skipped.` });
      skippedCount += 1;
      continue;
    }

    // Blank group → default
    if (!groupRaw) {
      groupRaw = 'Other/Unmapped';
      warnings.push({ row: i + 1, message: `"${payerRaw}" has no group specified — assigned to Other/Unmapped.` });
    }

    // Truncate long group names
    if (groupRaw.length > MAX_GROUP_LEN) {
      groupRaw = groupRaw.slice(0, MAX_GROUP_LEN);
      warnings.push({ row: i + 1, message: `Group name truncated to ${MAX_GROUP_LEN} characters.` });
    }

    // Track new groups
    if (!groupSet.has(groupRaw.toLowerCase())) {
      groupSet.add(groupRaw.toLowerCase());
      newGroupSet.add(groupRaw);
    }

    // Handle duplicate payers — last row wins
    const lowerPayer = payerRaw.toLowerCase();
    if (seenPayers.has(lowerPayer)) {
      warnings.push({ row: i + 1, message: `Duplicate entry for "${payerRaw}" — using this row instead.` });
      const prevIdx = seenPayers.get(lowerPayer)!;
      rows[prevIdx] = { payer: payerRaw, group: groupRaw, rowKind: 'unchanged' };
    } else {
      seenPayers.set(lowerPayer, rows.length);
      rows.push({ payer: payerRaw, group: groupRaw, rowKind: 'unchanged' });
    }
  }

  if (rows.length === 0 && warnings.length === 0) {
    warnings.push({ row: 0, message: 'File contains no data rows after the header.' });
  }

  return { rows, warnings, newGroups: Array.from(newGroupSet), skippedCount };
};

// ---------- Component ----------

const PayerGroupingUpload: React.FC<PayerGroupingUploadProps> = ({
  existingPayers,
  existingGroups,
  onApply,
  onUploadStateChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [fileName, setFileName] = useState('');

  const onUploadStateChangeRef = useRef(onUploadStateChange);
  onUploadStateChangeRef.current = onUploadStateChange;
  const uploadCounterRef = useRef(0);

  // Build a set of known payer names for validation
  const existingPayerNameSet = useMemo(
    () => new Set(existingPayers.map((p) => p.insurancePayerPrimary.toLowerCase())),
    [existingPayers],
  );

  const resetState = useCallback(() => {
    setParseResult(null);
    setFileName('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_FILE_SIZE_BYTES) {
        setFileName(file.name);
        setParseResult({
          rows: [],
          warnings: [{ row: 0, message: `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 10 MB.` }],
          newGroups: [],
          skippedCount: 0,
        });
        e.target.value = '';
        return;
      }

      uploadCounterRef.current += 1;
      const thisUpload = uploadCounterRef.current;

      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (uploadCounterRef.current !== thisUpload) return;
        try {
          const data = new Uint8Array(evt.target?.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: 'array' });
          const result = parseWorkbook(wb, existingGroups, existingPayerNameSet);

          // Enrich each row with rowKind by comparing against current payer→group assignments
          const currentGroupMap = new Map(
            existingPayers.map((p) => [p.insurancePayerPrimary.toLowerCase(), p.assignedMasterPayorGroup.toLowerCase()])
          );
          const newGroupsLower = new Set(result.newGroups.map((g) => g.toLowerCase()));
          const enrichedRows: ParsedRow[] = result.rows.map((r) => {
            if (newGroupsLower.has(r.group.toLowerCase())) {
              return { ...r, rowKind: 'new-group' as RowKind };
            }
            const currentGroup = currentGroupMap.get(r.payer.toLowerCase());
            if (!currentGroup || currentGroup !== r.group.toLowerCase()) {
              return { ...r, rowKind: 'changed' as RowKind };
            }
            return { ...r, rowKind: 'unchanged' as RowKind };
          });

          setParseResult({ ...result, rows: enrichedRows });
        } catch {
          setParseResult({
            rows: [],
            warnings: [{ row: 0, message: 'Could not read this file. Make sure it is a valid Excel (.xlsx/.xls) or CSV file and is not password-protected.' }],
            newGroups: [],
            skippedCount: 0,
          });
        }
      };
      reader.readAsArrayBuffer(file);
      // Reset so the same file can be re-selected
      e.target.value = '';
    },
    [existingGroups, existingPayerNameSet, existingPayers],
  );

  const handleApply = useCallback(() => {
    if (!parseResult) return;

    // Build a map of uploaded assignments (only contains recognized payers)
    const uploadedMap = new Map(
      parseResult.rows.map((r) => [r.payer.toLowerCase(), r.group]),
    );

    // Always merge: only update payers present in the upload
    const finalRows = existingPayers.map((ep) => {
      const uploadedGroup = uploadedMap.get(ep.insurancePayerPrimary.toLowerCase());
      return {
        insurancePayerPrimary: ep.insurancePayerPrimary,
        assignedMasterPayorGroup: uploadedGroup ?? ep.assignedMasterPayorGroup,
      };
    });

    // Build full group list (existing + any new groups from the upload)
    const groupSet = new Set<string>(existingGroups);
    for (const r of finalRows) {
      groupSet.add(r.assignedMasterPayorGroup);
    }

    onApply(finalRows, Array.from(groupSet));
    resetState();
  }, [parseResult, existingPayers, existingGroups, onApply, resetState]);

  useEffect(() => {
    if (parseResult === null) {
      onUploadStateChangeRef.current?.(null);
    } else {
      onUploadStateChangeRef.current?.({
        onDiscard: resetState,
        onApply: handleApply,
        canApply: parseResult.rows.length > 0,
        onUploadDifferent: () => {
          resetState();
          setTimeout(() => fileInputRef.current?.click(), 0);
        },
      });
    }
    return () => { onUploadStateChangeRef.current?.(null); };
  }, [parseResult, resetState, handleApply]);

  const handleDownloadTemplate = useCallback(() => {
    const templateRows = existingPayers.map((r) => ({
      'Insurance Payer': r.insurancePayerPrimary,
      'Master Payer Group': r.assignedMasterPayorGroup,
    }));

    if (templateRows.length === 0) {
      templateRows.push({ 'Insurance Payer': '', 'Master Payer Group': '' });
    }

    const ws = XLSX.utils.json_to_sheet(templateRows);
    ws['!cols'] = [{ wch: 40 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payer Groupings');
    XLSX.writeFile(wb, 'payer-groupings-template.xlsx');
  }, [existingPayers]);

  const hasErrors =
    parseResult !== null &&
    parseResult.rows.length === 0 &&
    parseResult.warnings.length > 0;


  return (
    <div className="payer-upload-tab-body">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        aria-label="Upload payer groupings file"
      />

      {/* Instructions — collapsible, hidden once a file is parsed */}
      {!parseResult && (
        <>
          <details className="payer-upload-instructions">
            <summary className="payer-upload-instructions-summary">
              <i className="pi pi-info-circle" />
              <span>How to bulk-upload payer groupings</span>
              <i className="pi pi-chevron-down payer-upload-chevron" />
            </summary>

            <div className="payer-upload-instructions-body">
              <div className="payer-upload-steps">
                <div className="payer-upload-step">
                  <span className="payer-upload-step-num">1</span>
                  <div className="payer-upload-step-body">
                    <span className="payer-upload-step-title">Download your current assignments</span>
                    <span className="payer-upload-step-desc">Get an Excel file pre-filled with every payer and its current group.</span>
                  </div>
                </div>
                <div className="payer-upload-step">
                  <span className="payer-upload-step-num">2</span>
                  <div className="payer-upload-step-body">
                    <span className="payer-upload-step-title">Edit the <strong>Master Payer Group</strong> column</span>
                    <span className="payer-upload-step-desc">Change group values in the spreadsheet. New group names are created automatically.</span>
                  </div>
                </div>
                <div className="payer-upload-step">
                  <span className="payer-upload-step-num">3</span>
                  <div className="payer-upload-step-body">
                    <span className="payer-upload-step-title">Upload the modified file</span>
                    <span className="payer-upload-step-desc">Only payers in your practice data will be updated. Others are skipped.</span>
                  </div>
                </div>
              </div>

              <div className="payer-upload-own-file">
                <p className="payer-upload-own-file-heading">
                  <i className="pi pi-file-edit" />
                  Uploading your own file?
                </p>
                <p className="payer-upload-own-file-desc">
                  Your spreadsheet needs two columns with the following headers:
                </p>
                <div className="payer-upload-col-list">
                  <div className="payer-upload-col-item">
                    <span className="payer-upload-col-name">Insurance Payer</span>
                    <span className="payer-upload-col-desc">— payer name from your practice data, e.g. BCBS Preferred, Cigna, Charity Care</span>
                  </div>
                  <div className="payer-upload-col-item">
                    <span className="payer-upload-col-name">Master Payer Group</span>
                    <span className="payer-upload-col-desc">— the payer mix heading to group under, e.g. Blue Cross Blue Shield, Aetna, Ambetter</span>
                  </div>
                </div>
              </div>
            </div>
          </details>

          {/* Action buttons */}
          <div className="payer-upload-actions">
            <Button
              label="Download Current Assignments"
              icon="pi pi-download"
              severity="secondary"
              outlined
              onClick={handleDownloadTemplate}
            />
            <Button
              label="Upload Updated File"
              icon="pi pi-upload"
              onClick={() => fileInputRef.current?.click()}
            />
          </div>
        </>
      )}

      {/* Result section — shown after a file is parsed */}
      {parseResult && (
        <>

          {hasErrors ? (
            <Message
              severity="error"
              text={
                parseResult!.warnings.find((w) => w.row === 0)?.message
                ?? (parseResult!.skippedCount > 0
                  ? `None of the payers in this file match your practice data. Check that you're uploading a file for the correct account.`
                  : parseResult!.warnings[0]?.message ?? 'Could not parse file.')
              }
              className="w-full"
            />
          ) : (
            <div className="payer-upload-result">
              {/* Summary — collapsible like the warnings row */}
              <details className="payer-upload-summary" open>
                <summary className="payer-upload-summary-toggle">
                  <i className="pi pi-file" />
                  <span className="payer-upload-summary-file">{fileName}</span>
                  <i className="pi pi-chevron-down payer-upload-summary-chevron" />
                </summary>

                {(() => {
                  const newGroupCount = parseResult.rows.filter((r) => r.rowKind === 'new-group').length;
                  const changedCount  = parseResult.rows.filter((r) => r.rowKind === 'changed').length;
                  const unchangedCount = parseResult.rows.filter((r) => r.rowKind === 'unchanged').length;
                  return (
                    <div className="payer-upload-summary-body">
                      <div className="payer-upload-summary-row">
                        <i className="pi pi-check-circle payer-upload-icon-success" />
                        <span>
                          <strong>{parseResult.rows.length}</strong> insurance payer{parseResult.rows.length !== 1 ? 's' : ''} recognised from file
                        </span>
                      </div>

                      {newGroupCount > 0 && (
                        <div className="payer-upload-summary-row">
                          <i className="pi pi-plus-circle payer-upload-icon-new" />
                          <span>
                            <strong>{newGroupCount}</strong> payer{newGroupCount !== 1 ? 's' : ''} will be assigned to a new group
                            <span className="payer-upload-new-groups-list">
                              ({parseResult.newGroups.join(', ')})
                            </span>
                          </span>
                        </div>
                      )}

                      {changedCount > 0 && (
                        <div className="payer-upload-summary-row">
                          <i className="pi pi-pencil payer-upload-icon-changed" />
                          <span>
                            <strong>{changedCount}</strong> payer{changedCount !== 1 ? 's' : ''} will be moved to a different group
                          </span>
                        </div>
                      )}

                      {unchangedCount > 0 && (
                        <div className="payer-upload-summary-row">
                          <i className="pi pi-check-circle payer-upload-icon-success" />
                          <span>
                            <strong>{unchangedCount}</strong> payer{unchangedCount !== 1 ? 's' : ''} already in correct group — no change
                          </span>
                        </div>
                      )}

                      {parseResult.skippedCount > 0 && (
                        <div className="payer-upload-summary-row">
                          <i className="pi pi-minus-circle payer-upload-icon-skip" />
                          <span>
                            <strong>{parseResult.skippedCount}</strong> payer{parseResult.skippedCount !== 1 ? 's' : ''} skipped — not found in practice data
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </details>

              {parseResult.warnings.length > 0 && (
                <details className="payer-upload-warnings">
                  <summary className="payer-upload-warnings-summary">
                    <i className="pi pi-exclamation-triangle" />
                    {parseResult.warnings.length} warning{parseResult.warnings.length !== 1 ? 's' : ''}
                    <i className="pi pi-chevron-down payer-upload-warnings-chevron" />
                  </summary>
                  <ul className="payer-upload-warnings-body">
                    {parseResult.warnings.map((w, i) => (
                      <li key={i} className="payer-upload-warning-item">
                        {w.row > 0 && <span className="payer-upload-warning-row">Row {w.row}:</span>}
                        {w.message}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              {/* Preview table */}
              <div className="payer-upload-table-wrap">
                <table className="payer-upload-table">
                  <thead>
                    <tr>
                      <th>Insurance Payer</th>
                      <th>Assigned Group</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parseResult.rows.slice(0, 100).map((r, i) => (
                      <tr key={i} className={`payer-upload-row-${r.rowKind}`}>
                        <td>{r.payer}</td>
                        <td>
                          {r.rowKind === 'new-group' && (
                            <i
                              className="pi pi-plus-circle payer-upload-icon-new payer-upload-row-icon"
                              data-pr-tooltip="Assigned to a new group"
                              data-pr-position="top"
                            />
                          )}
                          {r.rowKind === 'changed' && (
                            <i
                              className="pi pi-pencil payer-upload-icon-changed payer-upload-row-icon"
                              data-pr-tooltip="Moving to a different group"
                              data-pr-position="top"
                            />
                          )}
                          {/* {r.rowKind === 'unchanged' && (
                            <i
                              className="pi pi-check payer-upload-icon-unchanged payer-upload-row-icon"
                              data-pr-tooltip="Already in the correct group — no change"
                              data-pr-position="top"
                            />
                          )} */}
                          {r.group}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <Tooltip target=".payer-upload-row-icon" />
                {parseResult.rows.length > 100 && (
                  <p className="text-500 text-center mt-2" style={{ fontSize: '0.8125rem' }}>
                    Showing first 100 of {parseResult.rows.length} rows
                  </p>
                )}
              </div>


            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PayerGroupingUpload;
